import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { Spin, Button, Tooltip } from "antd";
import { ArrowLeftOutlined, RightOutlined } from "@ant-design/icons";
import DOMPurify from "dompurify";
import Service from "../../service";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import usePublicBranding from "./usePublicBranding";
import PublicCareerLayout from "./PublicCareerLayout";
import JobMeta from "./JobMeta";

const sanitize = (html) => DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } });

const skillsText = (skills) => (Array.isArray(skills) && skills.length ? skills.join(", ") : null);

/**
 * Public, unauthenticated job description page reached from the job's
 * shareable link. Applying happens on the dedicated /apply page.
 */
const CareerPageJobDetail = () => {
  const { companyslug, publicSlug } = useParams();
  const history = useHistory();
  const { branding } = usePublicBranding(companyslug);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchJob = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.publicJobDetail(companyslug, publicSlug),
      });
      if (response?.data?.status === 200) {
        setJob(response.data.data);
      } else {
        setError(response?.data?.message || "This job could not be found.");
      }
    } catch (err) {
      setError("Unable to load this job right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [companyslug, publicSlug]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const applyPath = `/${companyslug}/careers/${publicSlug}/apply`;
  const goToApply = () => history.push(applyPath);

  if (loading) {
    return (
      <PublicCareerLayout companyslug={companyslug} branding={branding}>
        <div className="career-state">
          <Spin size="large" />
        </div>
      </PublicCareerLayout>
    );
  }

  if (error || !job) {
    return (
      <PublicCareerLayout companyslug={companyslug} branding={branding} title="Job not found">
        <div className="career-state">
          <NodataFoundIcon />
          <p>{error || "This job could not be found."}</p>
          <Link to={`/${companyslug}/careers`}>View all openings</Link>
        </div>
      </PublicCareerLayout>
    );
  }

  const requiredSkills = skillsText(job.required_skills);
  const preferredSkills = skillsText(job.preferred_skills);
  const similarJobs = job.similar_jobs || [];

  return (
    <PublicCareerLayout
      companyslug={companyslug}
      branding={branding}
      title={job.title}
      hero={
        <>
          <h1 className="career-hero-title">{job.title}</h1>
          <JobMeta job={job} />
        </>
      }
    >
      {/* <Link to={`/${companyslug}/careers`} className="career-back-link">
        <ArrowLeftOutlined /> Back to all openings
      </Link> */}

      <div className="career-layout">
        <section className="career-card career-job-detail">
          {job.job_description ? (
            <>
              <h2 className="career-section-title">Job Description</h2>
              <div className="career-rich-text" dangerouslySetInnerHTML={{ __html: sanitize(job.job_description) }} />
            </>
          ) : null}

          {job.qualification ? (
            <>
              <h2 className="career-section-title">Qualification</h2>
              <div className="career-rich-text" dangerouslySetInnerHTML={{ __html: sanitize(job.qualification) }} />
            </>
          ) : null}

          {requiredSkills ? (
            <>
              <h2 className="career-section-title">Required Skills</h2>
              <p className="career-text">{requiredSkills}</p>
            </>
          ) : null}

          {preferredSkills ? (
            <>
              <h2 className="career-section-title">Preferred Skills</h2>
              <p className="career-text">{preferredSkills}</p>
            </>
          ) : null}
        </section>

        <aside className="career-side">
          <div className="career-card career-apply-card">
            <Button type="primary" size="large" block onClick={goToApply}>
              Apply for this job
            </Button>
            <p className="career-apply-hint">Takes about 5 minutes. No account needed.</p>
          </div>

          <div className="career-card">
            <div className="career-side-head">
              <h2 className="career-side-title">Similar openings</h2>
              <Tooltip title="View all openings">
                <Link to={`/${companyslug}/careers`} className="career-side-arrow" aria-label="View all openings">
                  <RightOutlined />
                </Link>
              </Tooltip>
            </div>
            {similarJobs.length ? (
              <ul className="career-similar-list">
                {similarJobs.map((similar) => (
                  <li className="career-similar-item" key={similar._id}>
                    <Link to={`/${companyslug}/careers/${similar.public_slug}`} className="career-similar-title">
                      {similar.title}
                    </Link>
                    <JobMeta job={similar} compact />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="career-similar-empty">
                <div className="career-similar-empty-icon">
                  <NodataFoundIcon />
                </div>
                <p className="career-muted">No similar openings right now.</p>
              </div>
            )}
          </div>
        </aside>
      </div>

      <div className="career-apply-bar">
        <Button type="primary" size="large" block onClick={goToApply}>
          Apply for this job
        </Button>
      </div>
    </PublicCareerLayout>
  );
};

export default CareerPageJobDetail;
